/**
 * Utilidades de saneado y detección de enlaces para el editor de texto enriquecido.
 * Sin dependencias externas: se apoya en DOMParser + lista blanca de etiquetas.
 *
 * El HTML que sale de aquí es el que se guarda en el chat del ticket y se pinta
 * con dangerouslySetInnerHTML, así que solo puede contener formato inofensivo.
 */

// Etiquetas que sobreviven al saneado (el resto se "desenvuelve" o se elimina)
const TAGS_PERMITIDAS = new Set([
    'P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'S', 'STRIKE', 'DEL',
    'UL', 'OL', 'LI', 'A', 'BLOCKQUOTE', 'H1', 'H2', 'H3',
]);

// Etiquetas que se eliminan por completo, con su contenido incluido
const TAGS_ELIMINADAS = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'FORM', 'INPUT', 'BUTTON', 'META', 'LINK']);

// Bloques que convertimos a párrafo para mantener el mismo formato que producía Quill
const TAGS_A_PARRAFO = new Set(['DIV', 'SECTION', 'ARTICLE', 'PRE', 'ADDRESS']);

const PROTOCOLOS_PERMITIDOS = ['http:', 'https:', 'mailto:', 'tel:'];

// Detección de un enlace/email justo antes del cursor (autolink al escribir)
export const RE_URL_FINAL = /(?:https?:\/\/|www\.)[^\s<>"']+$/i;
export const RE_EMAIL_FINAL = /[^\s<>"'@]+@[^\s<>"'@]+\.[a-zA-Z]{2,}$/;

// Detección de enlaces/emails dentro de un texto plano (pegado)
const RE_ENLACES = /((?:https?:\/\/|www\.)[^\s<]+|[^\s<@]+@[^\s<@]+\.[a-zA-Z]{2,})/g;

export const escaparHtml = (texto) => String(texto ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * Convierte lo que el usuario escribe (o pega) en una URL absoluta segura.
 * Devuelve null si el protocolo no está permitido (p. ej. javascript:).
 */
export const normalizarUrl = (valor) => {
    const bruto = String(valor ?? '').trim();
    if (!bruto) return null;

    let candidato = bruto;
    if (/^www\./i.test(candidato)) {
        candidato = `https://${candidato}`;
    } else if (/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(candidato)) {
        candidato = `mailto:${candidato}`;
    } else if (!/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(candidato)) {
        candidato = `https://${candidato}`;
    }

    try {
        const url = new URL(candidato);
        return PROTOCOLOS_PERMITIDOS.includes(url.protocol) ? url.href : null;
    } catch (error) {
        return null;
    }
};

const desenvolver = (nodo) => {
    const padre = nodo.parentNode;
    if (!padre) return;
    while (nodo.firstChild) padre.insertBefore(nodo.firstChild, nodo);
    padre.removeChild(nodo);
};

const limpiarNodo = (raiz, doc) => {
    Array.from(raiz.childNodes).forEach((nodo) => {
        if (nodo.nodeType === Node.TEXT_NODE) return;

        if (nodo.nodeType !== Node.ELEMENT_NODE) {
            nodo.remove(); // comentarios y demás
            return;
        }

        const tag = nodo.tagName;

        if (TAGS_ELIMINADAS.has(tag)) {
            nodo.remove();
            return;
        }

        limpiarNodo(nodo, doc);

        if (TAGS_A_PARRAFO.has(tag)) {
            const parrafo = doc.createElement('p');
            while (nodo.firstChild) parrafo.appendChild(nodo.firstChild);
            nodo.replaceWith(parrafo);
            return;
        }

        if (!TAGS_PERMITIDAS.has(tag)) {
            desenvolver(nodo);
            return;
        }

        const href = tag === 'A' ? normalizarUrl(nodo.getAttribute('href')) : null;
        Array.from(nodo.attributes).forEach((attr) => nodo.removeAttribute(attr.name));

        if (tag === 'A') {
            if (!href) {
                desenvolver(nodo);
                return;
            }
            nodo.setAttribute('href', href);
            nodo.setAttribute('target', '_blank');
            nodo.setAttribute('rel', 'noopener noreferrer');
        }
    });
};

/**
 * Limpia un fragmento HTML dejando solo formato permitido y enlaces seguros.
 */
export const sanitizarHtml = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
    limpiarNodo(doc.body, doc);
    return doc.body.innerHTML.trim();
};

const RE_HTML_COMO_TEXTO = /<\/?(?:p|div|span|br|ul|ol|li|strong|b|em|i|u|s|strike|del|a|blockquote|h[1-6])(?:\s[^<>]*?)?\s*\/?>/i;

const decodificarEntidades = (texto) => {
    const doc = new DOMParser().parseFromString(String(texto ?? ''), 'text/html');
    return doc.documentElement.textContent || '';
};

const quitarComillasDeEnvoltura = (texto) => String(texto ?? '')
    .replace(/^(\s*)["'](?=\s*<)/, '$1')
    .replace(/(>)\s*["'](\s*)$/, '$1$2');

/**
 * Corrige mensajes en los que otro editor o una integración guardó HTML
 * escapado como texto. Conserva la estructura útil y elimina estilos, código y
 * atributos ajenos antes de que el chat lo pinte con innerHTML.
 */
export const normalizarHtmlMensaje = (html) => {
    if (!html) return '';

    const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
    let corrigioHtmlEscapado = false;

    // Dos pasadas cubren también contenido que haya sido escapado más de una vez.
    for (let pasada = 0; pasada < 2; pasada += 1) {
        const paseo = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
        const nodos = [];
        while (paseo.nextNode()) nodos.push(paseo.currentNode);

        let huboCambios = false;
        nodos.forEach((nodo) => {
            const valor = nodo.nodeValue || '';
            const decodificado = quitarComillasDeEnvoltura(
                RE_HTML_COMO_TEXTO.test(valor) ? valor : decodificarEntidades(valor)
            );
            if (!RE_HTML_COMO_TEXTO.test(decodificado)) return;

            const fragmentoDoc = new DOMParser().parseFromString(`<body>${decodificado}</body>`, 'text/html');
            const fragmento = doc.createDocumentFragment();
            Array.from(fragmentoDoc.body.childNodes).forEach((hijo) => {
                fragmento.appendChild(doc.importNode(hijo, true));
            });
            nodo.replaceWith(fragmento);
            huboCambios = true;
            corrigioHtmlEscapado = true;
        });

        if (!huboCambios) break;
    }

    const saneado = sanitizarHtml(doc.body.innerHTML);
    if (!corrigioHtmlEscapado) return saneado;

    // Al reinsertar bloques que venían dentro de un <p>, el parser puede crear
    // párrafos vacíos de cierre. No representan saltos escritos por el usuario.
    const resultado = new DOMParser().parseFromString(`<body>${saneado}</body>`, 'text/html');
    resultado.body.querySelectorAll('p:empty').forEach((parrafo) => parrafo.remove());
    return resultado.body.innerHTML.trim();
};

/**
 * Un mensaje se considera vacío si no tiene texto visible (aunque traiga <p><br></p>).
 */
export const esHtmlVacio = (html) => {
    if (!html) return true;
    const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
    const texto = (doc.body.textContent || '').replace(/\u00A0/g, ' ').trim();
    return texto === '';
};

// Estructuras que ya ocupan sitio en el editor aunque todavía no tengan texto
const SELECTOR_CON_CUERPO = 'ul, ol, li, blockquote, h1, h2, h3, img, table';

/**
 * Igual que `esHtmlVacio`, pero pensado para el marcador de posición: una lista
 * recién insertada no tiene texto y aun así no debe dejar el placeholder encima.
 */
export const esHtmlSinContenidoVisible = (html) => {
    if (!html) return true;

    const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
    if (doc.body.querySelector(SELECTOR_CON_CUERPO)) return false;

    return (doc.body.textContent || '').replace(/\u00A0/g, ' ').trim() === '';
};

/**
 * Convierte texto plano en HTML: escapa, respeta saltos de línea y autoenlaza.
 */
export const htmlDesdeTextoPlano = (texto) => {
    const contenido = String(texto ?? '');
    if (!contenido.trim()) return '';

    const enlazarLinea = (linea) => escaparHtml(linea).replace(RE_ENLACES, (coincidencia) => {
        // La coincidencia viene escapada: los & de la query están como &amp;
        const limpio = coincidencia.replace(/&amp;/g, '&');
        const recorte = limpio.match(/[.,;:!?)\]}]+$/);
        const visible = recorte ? limpio.slice(0, -recorte[0].length) : limpio;
        const cola = recorte ? recorte[0] : '';
        const href = normalizarUrl(visible);
        if (!href) return coincidencia;
        return `<a href="${escaparHtml(href)}" target="_blank" rel="noopener noreferrer">${escaparHtml(visible)}</a>${cola}`;
    });

    return contenido
        .split(/\r?\n/)
        .map((linea) => `<p>${linea.trim() ? enlazarLinea(linea) : '<br>'}</p>`)
        .join('');
};
